import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import IntakeTimePicker from "../IntakeTimePicker";

const toMinutes = (hhmm) => {
  const [h, m] = String(hhmm).split(":").map(Number);
  return h * 60 + m;
};

/**
 * Meal intake window editor opened from the orange summary card. The window
 * lives on the target plan meal, so it is shared by every option of the meal
 * type. Times in and out are "HH:MM" ("" = not set), saved as-is with no
 * timezone conversion.
 *
 * @param {string} props.mealTypeName - e.g. "Breakfast"
 * @param {{ start: string, end: string }} props.intakeWindow - current window
 * @param {({ start, end }) => (void|Promise)} props.onSave
 */
export default function MealSlotIntakeTimeModal({
  isOpen,
  onClose,
  mealTypeName,
  intakeWindow,
  onSave,
}) {
  const initialStart = intakeWindow?.start || "";
  const initialEnd = intakeWindow?.end || "";
  // Seeded once: the caller mounts this per open, and a background sync
  // landing mid-edit must not overwrite what the user is picking.
  const [start, setStart] = useState(initialStart);
  const [end, setEnd] = useState(initialEnd);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const changed = start !== initialStart || end !== initialEnd;
  // A window is both times or neither; a lone start or end can't be saved.
  const incomplete = Boolean(start) !== Boolean(end);
  const invalidOrder = Boolean(start && end) && toMinutes(end) <= toMinutes(start);
  const canSave = changed && !incomplete && !invalidOrder;
  const hasAnyTime = Boolean(start || end);

  const handleClear = () => {
    setStart("");
    setEnd("");
  };

  const handleSave = () => {
    if (!canSave) return;
    onSave({ start, end });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">{mealTypeName}</h3>
            <p className="text-sm text-gray-500">Intake window</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start</label>
              <IntakeTimePicker
                value={start}
                onChange={setStart}
                placeholder="Not set"
                clearable={false}
                hour12
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End</label>
              <IntakeTimePicker
                value={end}
                onChange={setEnd}
                placeholder="Not set"
                defaultTime={start || "09:00"}
                clearable={false}
                hour12
                className="w-full"
              />
            </div>
          </div>

          <div className="flex items-start justify-between gap-3">
            {incomplete ? (
              <p className="text-xs text-red-600">Set both start and end, or clear both.</p>
            ) : invalidOrder ? (
              <p className="text-xs text-red-600">End time must be after the start time.</p>
            ) : (
              <p className="text-xs text-gray-500">
                Applies to all options of {mealTypeName}.
              </p>
            )}
            {hasAnyTime ? (
              <button
                type="button"
                onClick={handleClear}
                className="shrink-0 text-xs font-medium text-orange-primary hover:text-orange-600 hover:underline transition-colors"
              >
                Clear
              </button>
            ) : null}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!canSave}
              className="flex-1 px-4 py-2 bg-orange-primary hover:bg-orange-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
